import { isNil } from 'lodash';
import { ICell } from 'models/interfaces/spreadsheet-types/core-types';
import { CAP_TABLE_SELECTION } from 'pages/Valuations/util/constants';
import { EquityAllocationCellCustomData } from 'pages/ValuationsAllocation/allocation/EquityAllocation/config';
import { ValuationSummaryCellCustomData } from 'pages/ValuationsAllocation/approaches/ValuationSummary/config';
import {
  EQUITY_ALLOCATION_SPREADSHEET_ALLOCATION_METHOD,
  EQUITY_ALLOCATION_SPREADSHEET_CAP_TABLE_SELECTION,
  EQUITY_ALLOCATION_SPREADSHEET_MATURITY,
  EQUITY_ALLOCATION_SPREADSHEET_SCENARIO_WEIGHTING_PROBABILITY,
  EQUITY_ALLOCATION_SPREADSHEET_VOLATILITY,
  EQUITY_ALLOCATION_SPREADSHEET_VOLATILITY_SOURCE,
} from 'pages/ValuationsAllocation/common/constants/equityAllocation/sheetAliases';
import { VALUATION_SUMMARY_SPREADSHEET_SCENARIO_WEIGHTING_PROBABILITY } from 'pages/ValuationsAllocation/common/constants/valuationSummary/sheetAliases';
import { getObjectValue } from 'utilities';

/**
 * Validates if a cell is a scenario weight cell.
 * A scenario weight cell is identified by being an approach cell with a scenario ID or ref.
 */
export const isScenarioWeightCell = (cell: ICell<ValuationSummaryCellCustomData>): boolean => {
  const { isValid, y: columnNumber, data: cellData } = getObjectValue(cell);
  const { isApproach = false, scenarioId, scenarioRef } = getObjectValue(cellData);

  const isScenarioWeight = isApproach && (scenarioId || scenarioRef);

  return Boolean(isScenarioWeight && !isNil(columnNumber) && isValid);
};

/**
 * Validates if a cell is an allocation method cell.
 */
export const isAllocationMethodCell = (cell: ICell<EquityAllocationCellCustomData>): boolean => {
  const { alias, isValid, y: columnNumber } = getObjectValue(cell);

  return Boolean(
    alias && [EQUITY_ALLOCATION_SPREADSHEET_ALLOCATION_METHOD].includes(alias) && !isNil(columnNumber) && isValid
  );
};

/**
 * Validates if a cell is a cap table selection cell.
 * Matches both DataEditor customKey and row alias for reliability.
 */
export const isCapTableSelectionCell = (cell: ICell<EquityAllocationCellCustomData>): boolean => {
  const { alias, isValid, y: columnNumber, customKey } = getObjectValue(cell);

  const isEquityAllocationCapTableCell =
    customKey === CAP_TABLE_SELECTION || alias === EQUITY_ALLOCATION_SPREADSHEET_CAP_TABLE_SELECTION;

  return Boolean(isEquityAllocationCapTableCell && !isNil(columnNumber) && isValid);
};

export const isMaturityCell = (cell: ICell<EquityAllocationCellCustomData>): boolean => {
  const { alias, isValid, y: columnNumber } = getObjectValue(cell);

  return Boolean(alias && [EQUITY_ALLOCATION_SPREADSHEET_MATURITY].includes(alias) && !isNil(columnNumber) && isValid);
};

/**
 * Validates if a cell is a volatility cell (either volatility or volatility source).
 */
export const isVolatilityCell = (cell: ICell<EquityAllocationCellCustomData>): boolean => {
  const { alias, isValid, y: columnNumber } = getObjectValue(cell);

  return Boolean(
    alias &&
      [EQUITY_ALLOCATION_SPREADSHEET_VOLATILITY, EQUITY_ALLOCATION_SPREADSHEET_VOLATILITY_SOURCE].includes(alias) &&
      !isNil(columnNumber) &&
      isValid
  );
};

/**
 * Validates if a cell is a scenario weighting probability cell.
 * Can be from either valuation summary or equity allocation sheet.
 */
export const isScenarioWeightingProbabilityCell = (
  cell: ICell<ValuationSummaryCellCustomData | EquityAllocationCellCustomData>
): boolean => {
  const { alias, isValid, y: columnNumber } = getObjectValue(cell);

  return Boolean(
    alias &&
      [
        VALUATION_SUMMARY_SPREADSHEET_SCENARIO_WEIGHTING_PROBABILITY,
        EQUITY_ALLOCATION_SPREADSHEET_SCENARIO_WEIGHTING_PROBABILITY,
      ].includes(alias) &&
      !isNil(columnNumber) &&
      isValid
  );
};
